import { DateTime } from 'luxon';

export type StorageHook = (storageKey: string) => [SnoozeSnapshot | undefined, (value: SnoozeSnapshot) => void];

type SnoozeStateConfig = {
  storageKey: string;
  useStorage: StorageHook;
  snoozeDurationMs?: number;
  maxSnoozeCount?: number;
};

type SnoozeSnapshot = {
  snoozedAtISO: string | null;
  snoozedCount: number;
};

const decodeSnoozeSnapshot = (storedValue: string | undefined | SnoozeSnapshot): SnoozeSnapshot => {
  if (!storedValue) {
    return { snoozedAtISO: null, snoozedCount: 0 };
  }

  if (typeof storedValue === 'string') {
    return { snoozedAtISO: storedValue, snoozedCount: 0 };
  }

  return storedValue;
};

/**
 * Shared dismissal/snooze state for promotional popups, stored in localStorage.
 * @param {SnoozeStateConfig} config
 * @param {string} config.storageKey - localStorage key (or key accessor for user-specific storage hooks)
 * @param {StorageHook} config.useStorage - `useLocalStorage` or `useUserSpecificLocalStorage` from use-local-storage
 * @param {number} [config.snoozeDurationMs] - window added to the stored dismissed-at timestamp.
 *   Omit when the stored timestamp is already an absolute snoozed-until value (the DDP legacy format).
 * @param {number} [config.maxSnoozeCount] - once the stored dismissal count reaches this, the popup stays dismissed permanently
 * @returns {{ isSnoozed: boolean, snooze: () => void, snoozePermanently: () => void }}
 */
export const useSnoozeState = (config: SnoozeStateConfig) => {
  const { storageKey, useStorage, snoozeDurationMs, maxSnoozeCount } = config;
  const [storedValue, setValue] = useStorage(storageKey);
  const { snoozedAtISO, snoozedCount } = decodeSnoozeSnapshot(storedValue);

  const isWithinSnoozeWindow = () => {
    if (snoozedAtISO == null) {
      return false;
    }
    const dismissedAtMs = DateTime.fromISO(snoozedAtISO).toMillis();
    if (Number.isNaN(dismissedAtMs)) {
      return false;
    }

    const snoozedUntilMs = snoozeDurationMs == null ? dismissedAtMs : dismissedAtMs + snoozeDurationMs;
    return DateTime.now().toMillis() < snoozedUntilMs;
  };

  const isPastDismissalCap = maxSnoozeCount != null && snoozedCount >= maxSnoozeCount;
  const isInSnoozeWindow = isWithinSnoozeWindow();
  const isSnoozed = isInSnoozeWindow || isPastDismissalCap;
  const snooze = () => {
    setValue({
      snoozedAtISO: DateTime.now().toISO(),
      snoozedCount: snoozedCount + 1,
    });
  };

  const snoozePermanently = () => {
    setValue({
      snoozedAtISO: DateTime.now().toISO(),
      snoozedCount: maxSnoozeCount ?? snoozedCount + 1,
    });
  };

  return { isSnoozed, snooze, snoozePermanently };
};
